import type { WorkItemDto } from '@customTypes/types';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { useAcceptWorkInviteMutation } from '@/features/work/api';
import { useRejectWorkInviteMutation } from '@/features/work/api/reject-work-invite';
import { useProfileStore } from '@/shared/store/profileStore';

import { interactionWorkItemsStatusFilters } from '../../constants';

export const useManageWorkInviteView = ({
  workItem
}: {
  workItem: WorkItemDto;
}) => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { currentSessionId } = useProfileStore();
  const rejectWorkInviteMutation = useRejectWorkInviteMutation();
  const acceptWorkInviteMutation = useAcceptWorkInviteMutation();

  useEffect(() => {
    notifications.show({
      title: formatMessage({ id: 'notifications.giveResponseFormWorkItem' }),
      ...notificationStyleConfig
    });
  }, []);

  const rejectInvite = async () => {
    await rejectWorkInviteMutation.mutateAsync(
      {
        params: {
          workInviteId: workItem.workItemId.toString()
        }
      },
      {
        onSuccess: () => {
          notifications.show({
            title: formatMessage({
              id: 'notifications.workInviteRejected'
            }),
            ...notificationStyleConfig
          });
          if (currentSessionId) {
            queryClient.invalidateQueries({
              queryKey: [
                'ArtistWorkItemsList',
                currentSessionId.toString(),
                interactionWorkItemsStatusFilters
              ],
              exact: true
            });
            router.history.back();
          }
        }
      }
    );

    queryClient.invalidateQueries({
      queryKey: ['ShowWorkInvite', workItem.workItemId.toString()]
    });
  };
  const acceptInvite = async () => {
    await acceptWorkInviteMutation.mutateAsync(
      {
        params: {
          workInviteId: workItem.workItemId.toString()
        }
      },
      {
        onSuccess: () => {
          notifications.show({
            title: formatMessage({
              id: 'notifications.workStartOnArtistArrivalConfirm'
            }),
            ...notificationStyleConfig
          });
        }
      }
    );
    if (currentSessionId) {
      queryClient.invalidateQueries({
        queryKey: [
          'ArtistWorkItemsList',
          currentSessionId.toString(),
          interactionWorkItemsStatusFilters
        ],
        exact: true
      });
    }
  };

  return {
    state: {},
    functions: { acceptInvite, rejectInvite }
  };
};
